/**
 * Drifting rocks: the small rocks of a field, and the rocks of the stream
 * (see AsteroidDef.drift). Each has a path, where it was made, that turned
 * round its eddy, or along the stream's belt, so where it is follows from
 * the clock and nothing need be sent. Rammed by a ship or bumped by another
 * rock it's knocked off its path (a Knock: the only state, kept by its area
 * and sent with snapshots), then swings back; a stream rock keeps some of a
 * sideways knock as a new place in the band (see Knock.o). Whatever stands
 * on a rock that moves goes with it, and so do ships moored by it; stepping
 * off, they keep its speed.
 */
import { CHARACTER, ROCK_MOTION, STREAM, WORLD_GEN } from "../constants";
import { clamp, v2, type Vec2 } from "../math";
import { bodyHitsGrid } from "./collision";
import { asteroidCenter, gravityReach, upVector } from "./character";
import { routeFor } from "./generate";
import { shipCenter, type AsteroidBody, type Knock, type ShipBody, type Sim } from "./ships";
import { bandWidth, streamAt, streamCoord, streamPoint } from "./stream";
import { asteroidPulls, type AsteroidDef, type PlayerState } from "./types";

/**
 * Where a drifting rock's grid origin is at time `t` (s) on its path: where
 * it was made, that turned round its eddy, or on the stream's belt (`settle`
 * blocks further across the band than it was made, see Knock.o).
 */
export function pathOrigin(def: AsteroidDef, t: number, settle = 0): Vec2 {
  // A rock that never drifts is where it was made (callers needn't check).
  const d = def.drift;
  if (!d) return v2(def.ox, def.oy);
  const st = d.stream;
  if (st) {
    const route = routeFor(st.key);
    const { p, dir, s } = streamAt(route, st.u, t);
    // Across the band, to the left of the way the belt goes; the band pinches at a narrows, and the rocks with it.
    const o = (st.o + settle) * (bandWidth(route, s) / STREAM.band);
    return v2(p.x - dir.y * o - def.w / 2, p.y + dir.x * o - def.h / 2);
  }
  const e = d.eddy;
  if (!e) return v2(d.x, d.y);
  const a = (e.spin * t) % (Math.PI * 2);
  const cos = Math.cos(a);
  const sin = Math.sin(a);
  const hx = d.x + def.w / 2 - e.x;
  const hy = d.y + def.h / 2 - e.y;
  return v2(e.x + hx * cos - hy * sin - def.w / 2, e.y + hx * sin + hy * cos - def.h / 2);
}

/** Where a drifting rock's grid origin is at time `t`: on its path, knocked off it by `knock`. */
export function rockOrigin(def: AsteroidDef, t: number, knock?: Knock): Vec2 {
  const o = pathOrigin(def, t, knock?.o ?? 0);
  return knock ? v2(o.x + knock.x, o.y + knock.y) : o;
}

/**
 * How fast a rock's path carries it (blocks/s): round its eddy, if it's in
 * one, or along the stream's belt: the belt's speed on the centre line, and
 * slower or faster across the band from it on a bend (inside or out).
 */
export function pathVelocity(def: AsteroidDef): Vec2 {
  const st = def.drift?.stream;
  if (st) {
    const route = routeFor(st.key);
    const { s, o } = streamCoord(route, asteroidCenter(def));
    const at = (x: number) => {
      const { p, dir } = streamPoint(route, x);
      return v2(p.x - dir.y * o, p.y + dir.x * o);
    };
    const a = at(s - 1);
    const b = at(s + 1);
    return v2(((b.x - a.x) / 2) * STREAM.speed, ((b.y - a.y) / 2) * STREAM.speed);
  }
  const e = def.drift?.eddy;
  if (!e) return v2();
  const c = asteroidCenter(def);
  return v2(-e.spin * (c.y - e.y), e.spin * (c.x - e.x));
}

/** Which way the belt goes where a stream rock is now (from where its grid is). */
function streamDirAt(def: AsteroidDef): Vec2 {
  const route = routeFor(def.drift!.stream!.key);
  const { s } = streamCoord(route, asteroidCenter(def));
  return streamAt(route, s, 0).dir;
}

/** The furthest a stream rock may settle across the band from where it was made (either way): inside the band, its own size allowed for. */
function settleRoom(def: AsteroidDef): [number, number] {
  const st = def.drift!.stream!;
  const room = Math.max(0, STREAM.band / 2 - def.radius * 1.25 - 2);
  return [-room - st.o, room - st.o];
}

/** A rock's weight (see ROCK_MOTION.density). */
export const rockMass = (def: AsteroidDef): number => ROCK_MOTION.density * Math.PI * def.radius ** 2;

/** A rock that moved, and how far. */
export interface RockMove {
  a: AsteroidBody;
  by: Vec2;
}

/** Put every drifting rock where it is at time `t` (s): on its path, plus its knock. Returns what moved, and how far. */
export function placeRocks(sim: Sim, t: number): RockMove[] {
  const moved: RockMove[] = [];
  for (const a of sim.asteroids) {
    if (!a.def.drift) continue;
    const o = rockOrigin(a.def, t, sim.knocks?.get(a.def.id));
    const by = v2(o.x - a.def.ox, o.y - a.def.oy);
    if (by.x === 0 && by.y === 0) continue;
    a.def.ox = o.x;
    a.def.oy = o.y;
    moved.push({ a, by });
  }
  return moved;
}

function knockOf(sim: Sim, id: string): Knock {
  sim.knocks ??= new Map();
  let k = sim.knocks.get(id);
  if (!k) sim.knocks.set(id, (k = { x: 0, y: 0, vx: 0, vy: 0 }));
  return k;
}

/** Is a knock over (the rock back on its path), with nothing but a settled offset left? */
export const knockAtRest = (k: Knock): boolean => k.x === 0 && k.y === 0 && k.vx === 0 && k.vy === 0;

function capSpeed(k: Knock): void {
  const s = Math.hypot(k.vx, k.vy);
  if (s <= ROCK_MOTION.maxSpeed) return;
  k.vx *= ROCK_MOTION.maxSpeed / s;
  k.vy *= ROCK_MOTION.maxSpeed / s;
}

/** Shove a drifting rock (`impulse`: weight times blocks/s, as Rapier gives it). Rocks that don't drift don't budge. */
export function knockRock(sim: Sim, def: AsteroidDef, impulse: Vec2): void {
  if (!def.drift) return;
  const k = knockOf(sim, def.id);
  const m = rockMass(def);
  k.vx += impulse.x / m;
  k.vy += impulse.y / m;
  capSpeed(k);
}

/**
 * Knocked rocks swing back toward their paths, slowing as they go, and are
 * let go once they're back. A stream rock's knock settles as it swings: a
 * share of its across-band part (STREAM.settle a second) sticks as the
 * rock's rest offset (Knock.o), clamped to the band, so the rock finds a
 * new place in the band and never leaves it; a settled rock keeps its knock
 * (at rest) for the offset. With `movable` (the server), they bump into
 * other rocks too: rocks it says no to (and rocks that don't drift) are
 * immovable, and the knocked rock bounces off them.
 */
export function stepKnocks(sim: Sim, dt: number, movable?: (a: AsteroidBody) => boolean): void {
  const knocks = sim.knocks;
  if (!knocks?.size) return;
  const M = ROCK_MOTION;
  let byId: Map<string, AsteroidBody> | undefined;
  for (const [id, k] of knocks) {
    const still = k.x === 0 && k.y === 0 && k.vx === 0 && k.vy === 0;
    if (still) continue;
    k.vx += (-M.spring * k.x - M.damping * k.vx) * dt;
    k.vy += (-M.spring * k.y - M.damping * k.vy) * dt;
    k.x += k.vx * dt;
    k.y += k.vy * dt;
    const d = Math.hypot(k.x, k.y);
    if (d > M.maxKnock) {
      // At the end of its tether: held there, and no further out.
      const nx = k.x / d;
      const ny = k.y / d;
      k.x = nx * M.maxKnock;
      k.y = ny * M.maxKnock;
      const out = k.vx * nx + k.vy * ny;
      if (out > 0) {
        k.vx -= nx * out;
        k.vy -= ny * out;
      }
    }
    // A stream rock: the sideways part of the knock settles into its place in the band (the rock doesn't move: what the knock loses, the offset gains).
    byId ??= new Map(sim.asteroids.map((a) => [a.def.id, a]));
    const a = byId.get(id);
    if (a?.def.drift?.stream && STREAM.settle > 0) {
      const dir = streamDirAt(a.def);
      const across = -k.x * dir.y + k.y * dir.x;
      const [lo, hi] = settleRoom(a.def);
      const want = clamp((k.o ?? 0) + across * STREAM.settle * dt, lo, hi);
      const moved = want - (k.o ?? 0);
      if (moved) {
        k.o = want;
        k.x += dir.y * moved;
        k.y -= dir.x * moved;
      }
    }
    if (d < M.rest && Math.hypot(k.vx, k.vy) < M.rest) {
      if (k.o) k.x = k.y = k.vx = k.vy = 0;
      else knocks.delete(id);
    }
  }
  if (movable) bump(sim, movable);
}

/** Knocked rocks that meet another rock (as discs, see ROCK_MOTION.size) bounce off it, and are pushed apart. */
function bump(sim: Sim, movable: (a: AsteroidBody) => boolean): void {
  const M = ROCK_MOTION;
  const knocks = sim.knocks!;
  const rocks = sim.asteroids;
  const moves = (a: AsteroidBody) => !!a.def.drift && movable(a);
  for (let i = 0; i < rocks.length; i++) {
    const a = rocks[i]!;
    const ka0 = knocks.get(a.def.id);
    if (!ka0 || knockAtRest(ka0) || !moves(a)) continue;
    const ca = asteroidCenter(a.def);
    const ra = a.def.radius * M.size;
    for (let j = 0; j < rocks.length; j++) {
      const b = rocks[j]!;
      if (b === a) continue;
      const bMoves = moves(b);
      // A pair of knocked rocks meets once, from the first of them.
      const kb0 = knocks.get(b.def.id);
      if (j < i && bMoves && kb0 && !knockAtRest(kb0)) continue;
      const cb = asteroidCenter(b.def);
      const rb = b.def.radius * M.size;
      const dx = cb.x - ca.x;
      const dy = cb.y - ca.y;
      const d = Math.hypot(dx, dy);
      if (d >= ra + rb || d === 0) continue;
      const nx = dx / d;
      const ny = dy / d;
      const ia = 1 / rockMass(a.def);
      const ib = bMoves ? 1 / rockMass(b.def) : 0;
      const ka = knockOf(sim, a.def.id);
      const kb = bMoves ? knockOf(sim, b.def.id) : undefined;
      const pa = pathVelocity(a.def);
      const pb = pathVelocity(b.def);
      const closing = (pb.x + (kb?.vx ?? 0) - pa.x - ka.vx) * nx + (pb.y + (kb?.vy ?? 0) - pa.y - ka.vy) * ny;
      if (closing < 0) {
        const imp = (-(1 + M.bounce) * closing) / (ia + ib);
        ka.vx -= nx * imp * ia;
        ka.vy -= ny * imp * ia;
        capSpeed(ka);
        if (kb) {
          kb.vx += nx * imp * ib;
          kb.vy += ny * imp * ib;
          capSpeed(kb);
        }
      }
      // Ease them apart (half the overlap a step), the lighter one more.
      const push = (ra + rb - d) * 0.5;
      const share = ia / (ia + ib);
      ka.x -= nx * push * share;
      ka.y -= ny * push * share;
      if (kb) {
        kb.x += nx * push * (1 - share);
        kb.y += ny * push * (1 - share);
      }
    }
  }
}

/** Nudges for "touching": a body this close to rock is standing on it, or leaning on it. */
const TOUCH = [v2(0.15, 0), v2(-0.15, 0), v2(0, 0.15), v2(0, -0.15)];

/**
 * Characters on drifting rocks that moved go with them: anyone in its area
 * where it was (its grid: the rock and the room round it to build in), so
 * floating by a small rock you go round with it rather than drift off it;
 * anyone touching it or that it moved into; anyone in a big rock's pull (feet
 * on the ground or not: a jump comes down where it went up); and anyone
 * manning a turret built on it. Aboard a ship, the ship carries you.
 *
 * Whoever steps or jumps off a rock that was carrying them (`dt` ago, the
 * last step) keeps its speed, as a ship let go of does (see carryShips):
 * off a stream rock that's a hop, which the air's drag takes off them in a
 * few rock-lengths, not a launch.
 */
export function carryRiders(moved: readonly RockMove[], players: Iterable<PlayerState>, dt = 0): void {
  for (const p of players) {
    if (p.frame) {
      p.rode = null;
      continue;
    }
    let on: RockMove | undefined;
    for (const m of moved) {
      if (p.gunningAsteroid ? p.gunningAsteroid !== m.a.def.id : !rides(p, m.a, m.by)) continue;
      on = m;
      break;
    }
    if (on) {
      p.position = v2(p.position.x + on.by.x, p.position.y + on.by.y);
      p.rode = on.a.def.id;
      continue;
    }
    if (p.rode) {
      // Off it: as fast as its path carries it (not by how far it moved this step: catching up with the clock, that can be a long way).
      const was = dt > 0 ? moved.find((m) => m.a.def.id === p.rode) : undefined;
      if (was) {
        const v = pathVelocity(was.a.def);
        p.velocity = v2(p.velocity.x + v.x, p.velocity.y + v.y);
      }
      p.rode = null;
    }
  }
}

/**
 * Ships nobody is flying go with the drifting rock they're moored by: one
 * whose area (where it was) their middle is in, or that their harpoon is
 * stuck in. So a ship parked at a base on a belt goes round the sun with it,
 * and its crew with it. A ship flown (or a pirate's) is never carried: it
 * threads the belts under its own steam. Let go of (someone took the helm,
 * or it drifted clear), it keeps the rock's speed, so it doesn't lurch.
 * Returns the ships it moved or let go of, whose bodies need their new pose.
 */
export function carryShips(moved: readonly RockMove[], ships: Iterable<ShipBody>, dt: number): ShipBody[] {
  const changed: ShipBody[] = [];
  for (const ship of ships) {
    const s = ship.state;
    const by = s.pilotId || s.pirate || ship.ghost ? undefined : moved.find((m) => moored(ship, m))?.by;
    if (by) {
      s.position = v2(s.position.x + by.x, s.position.y + by.y);
      ship.carried = v2(by.x / dt, by.y / dt);
    } else if (ship.carried) {
      s.velocity = v2(s.velocity.x + ship.carried.x, s.velocity.y + ship.carried.y);
      ship.carried = undefined;
    } else continue;
    changed.push(ship);
  }
  return changed;
}

function moored(ship: ShipBody, { a, by }: RockMove): boolean {
  const { def } = a;
  if (ship.state.tether?.rock === def.id) return true;
  const c = shipCenter(ship);
  const ox = def.ox - by.x;
  const oy = def.oy - by.y;
  return c.x >= ox && c.y >= oy && c.x < ox + def.w && c.y < oy + def.h;
}

/** The drifting rock a character (out in the world, not aboard a ship) is on, or in the pull of, if any: the one it would carry them with (see carryRiders). */
export function rockUnder(p: PlayerState, sim: Sim): AsteroidBody | undefined {
  if (p.frame) return undefined;
  return sim.asteroids.find((a) => a.def.drift && rides(p, a, v2()));
}

function rides(p: PlayerState, a: AsteroidBody, by: Vec2): boolean {
  const { def, grid } = a;
  const { x, y } = p.position;
  const ox = def.ox - by.x;
  const oy = def.oy - by.y;
  // Nowhere near it (or its pull), where it was or where it is (a rock catching up with the clock after a sleep moves a long way at once).
  const m = asteroidPulls(def) ? gravityReach(def) - def.radius * 1.25 + 4 : 4;
  const nearNew = !(x < def.ox - m || y < def.oy - m || x > def.ox + def.w + m || y > def.oy + def.h + m);
  const nearOld = !(x < ox - m || y < oy - m || x > ox + def.w + m || y > oy + def.h + m);
  if (!nearNew && !nearOld) return false;
  if (nearNew && bodyHitsGrid(p.position, p.up, grid, def.ox, def.oy)) return true;
  // In its area (by your middle), where it was.
  const U = upVector(p.up);
  const cx = x + U.x * (CHARACTER.height / 2);
  const cy = y + U.y * (CHARACTER.height / 2);
  if (cx >= ox && cy >= oy && cx < ox + def.w && cy < oy + def.h) return true;
  if (asteroidPulls(def) && Math.hypot(x - ox - def.w / 2, y - oy - def.h / 2) < gravityReach(def)) return true;
  return TOUCH.some((n) => bodyHitsGrid(v2(x + n.x, y + n.y), p.up, grid, ox, oy));
}
